import { createContext, useContext, type ReactNode } from 'react';

/**
 * Normalized gif shape the picker renders. The host adapts its own gif
 * provider (Klipy/Tenor/…) into this shape, so the package owns no HTTP
 * client, endpoint, or provider entities.
 */
export interface BizyGif {
	id: string;
	/** Thumbnail URL shown in the picker grid. */
	preview: string;
	/** Full gif URL (used as the attached preview). */
	full?: string;
	mp4?: string | null;
	description?: string;
	/**
	 * Host-defined attachment JSON forwarded verbatim into the
	 * `attach_gif` action payload when this gif is selected.
	 */
	attachment: Record<string, unknown>;
}

export interface BizyGifSearchResult {
	gifs: BizyGif[];
	providerName?: string;
	providerUrl?: string;
}

export type BizyGifSearch = (query: string) => Promise<BizyGifSearchResult>;

const defaultSearch: BizyGifSearch = async () => ({ gifs: [] });

const BizyGifContext = createContext<BizyGifSearch>(defaultSearch);

export function BizyGifProvider({ searchGifs, children }: { searchGifs?: BizyGifSearch; children: ReactNode }) {
	return <BizyGifContext.Provider value={searchGifs ?? defaultSearch}>{children}</BizyGifContext.Provider>;
}

export function useBizyGifSearch(): BizyGifSearch {
	return useContext(BizyGifContext);
}
